import { useMemo, useState } from 'react';
import { InformationCircleIcon, MapIcon } from '@heroicons/react/24/outline';
import type { CatalogRole, RouteInfo } from '../../../services/iam';
import { AccessCard } from '../primitives/AccessCard';
import { AccessCardSubsection } from '../primitives/AccessCardSubsection';
import { MethodTag } from '../primitives/MethodTag';
import { groupRoutesByDomain } from '../routes/routeDomainGrouping';

interface RoleRoutesCardProps {
    role: CatalogRole;
    /** Already filtered to routes this role's flattened set grants - see roleRoutes.ts. */
    grantedRoutes: readonly RouteInfo[];
}

// Card 4: routes downstream of Card 3's permissions, grouped by domain like
// the Routes page. Empty for every role until routes migrate - not a bug.
export function RoleRoutesCard({ role, grantedRoutes }: RoleRoutesCardProps) {
    const groups = useMemo(() => groupRoutesByDomain(grantedRoutes), [grantedRoutes]);
    const [closedDomains, setClosedDomains] = useState<ReadonlySet<string>>(new Set());

    const toggleDomain = (domain: string) => {
        setClosedDomains((prev) => {
            const next = new Set(prev);
            if (next.has(domain)) {
                next.delete(domain);
            } else {
                next.add(domain);
            }
            return next;
        });
    };

    return (
        <AccessCard
            icon={MapIcon}
            title="Routes this gives access to"
            count={grantedRoutes.length}
            noBodyPadding
            bodyClassName="flex flex-col"
        >
            {grantedRoutes.length === 0 ? (
                <div className="flex items-start gap-2.5 px-3.5 py-4">
                    <InformationCircleIcon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-500">
                        No route enforces any of these permissions yet, so this{' '}
                        {role.kind === 'bundle' ? 'bundle' : 'role'} currently grants access to
                        nothing.
                    </p>
                </div>
            ) : (
                groups.map((group, index) => (
                    <AccessCardSubsection
                        key={group.domain}
                        label={group.domain}
                        count={group.routes.length}
                        divider={index > 0}
                        collapsible
                        open={!closedDomains.has(group.domain)}
                        onToggle={() => toggleDomain(group.domain)}
                    >
                        {group.routes.map((route) => (
                            <div
                                key={`${route.method}-${route.path}`}
                                className="flex items-center gap-4 py-2 px-3.5 border-b border-gray-100 last:border-b-0"
                            >
                                <MethodTag method={route.method} />
                                <span className="font-mono text-[12px] text-gray-700 truncate">
                                    {route.path}
                                </span>
                            </div>
                        ))}
                    </AccessCardSubsection>
                ))
            )}
        </AccessCard>
    );
}
